import { FaPlus } from "react-icons/fa";
import Dropdown from "../../components/Form/Dropdown";
import CustomInput from "../../components/Form/Input";
import FormSectionHeading from "../../components/Form/FormSectionHeading";
import { CiCircleRemove } from "react-icons/ci";
import { categories } from "../../constants/commonConstants";

const ProfessionalDetailsSection = ({
  professionalDetails,
  setProfessionalDetails,
  professionalDetailsErrors,
  professionalDetailsTouched,
  setProfessionalDetailsTouched,
  setProfessionalDetailsErrors,
}) => {
  // FUNCTION: Add category
  const handleAddCategory = () => {
    let hasError = false;

    // Validate all categories
    const updatedErrors = professionalDetails?.map((category, i) => {
      let categoryError = !category.category ? "Category is required" : "";
      let categoryNameError = !category.categoryName
        ? "Category name is required"
        : "";
      let skillsError =
        category.skills.length === 0 ? "At least one skill is required" : "";
      let skills = category?.skills?.map((skill) => ({
        name: !skill.name ? "Skill is required" : "",
        description: !skill.description ? "Description is required" : "",
      }));

      // If any error exists, mark it
      if (
        categoryError ||
        categoryNameError ||
        skillsError ||
        skills.some((skill) => skill.name || skill.description)
      ) {
        hasError = true;
      }

      return {
        category: categoryError,
        categoryName: categoryNameError,
        skills: skills,
      };
    });

    // Stop if any validation error is found
    if (hasError) {
      setProfessionalDetailsErrors(updatedErrors);
      setProfessionalDetailsTouched((prev) =>
        prev.map((touched, i) => ({
          ...touched,
          category: !professionalDetails[i].category,
          categoryName: !professionalDetails[i].categoryName,
          skills: professionalDetails[i].skills.map((skill) => ({
            name: !skill.name,
            description: !skill.description,
          })),
        }))
      );
      return;
    }

    // If validation passes, add a new category
    setProfessionalDetails((prev) => [
      ...prev,
      {
        category: null,
        categoryName: "",
        skills: [
          {
            name: "",
            description: "",
          },
        ],
      },
    ]);

    setProfessionalDetailsErrors((prev) => [
      ...prev,
      {
        category: "",
        categoryName: "",
        skills: [
          {
            name: "",
            description: "",
          },
        ],
      },
    ]);

    setProfessionalDetailsTouched((prev) => [
      ...prev,
      {
        category: false,
        categoryName: false,
        skills: [
          {
            name: false,
            description: false,
          },
        ],
      },
    ]);
  };

  // FUNCTION: Handle category change
  const handleCategoryChange = (index, selectedCategory) => {
    console.log("selectedCategory", selectedCategory);

    setProfessionalDetails((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              category: selectedCategory,
              categoryName: selectedCategory?.name,
              skills: [
                {
                  name: "",
                  description: "",
                },
              ],
            }
          : item
      )
    );
  };

  // FUNCTION: Add skill
  const handleAddSkill = (categoryIndex) => {
    let hasError = false;

    // Validate all previous skills before adding a new one
    const updatedErrors = professionalDetails?.map((category, i) => {
      if (i !== categoryIndex) return category; // Keep other categories untouched

      let skills = category?.skills?.map((skill) => ({
        name: !skill.name ? "Skill is required" : "",
        description: !skill.description ? "Description is required" : "",
      }));

      // Check if any previous skill has an error
      if (skills.some((skill) => skill?.name || skill?.description)) {
        hasError = true;
      }

      return { ...category, skills };
    });

    // Stop adding a new skill if any previous skill has an error
    if (hasError) {
      setProfessionalDetailsErrors(updatedErrors);
      setProfessionalDetailsTouched((prev) =>
        prev.map((touched, i) =>
          i === categoryIndex
            ? {
                ...touched,
                skills: professionalDetails[i]?.skills?.map((skill) => ({
                  name: !skill.name,
                  description: !skill.description,
                })),
              }
            : touched
        )
      );
      return;
    }

    // Add new skill if validation passes
    setProfessionalDetails((prev) =>
      prev.map((item, i) =>
        i === categoryIndex
          ? {
              ...item,
              skills: [...item.skills, { name: "", description: "" }],
            }
          : item
      )
    );

    // Update error and touched states
    setProfessionalDetailsErrors((prev) =>
      prev.map((item, i) =>
        i === categoryIndex
          ? {
              ...item,
              skills: [...item.skills, { name: "", description: "" }],
            }
          : item
      )
    );

    setProfessionalDetailsTouched((prev) =>
      prev.map((item, i) =>
        i === categoryIndex
          ? {
              ...item,
              skills: [...item.skills, { name: false, description: false }],
            }
          : item
      )
    );
  };

  // FUNCTION: Handle skill change
  const handleSkillChange = (categoryIndex, skillIndex, field, value) => {
    // Check if the field is "name" and if the value is empty
    const isNameAndEmpty = field === "name" && (!value || value.trim() === "");

    // Clear errors and touched state for the field (and also description if name is empty)
    if (field === "description" || isNameAndEmpty) {
      setProfessionalDetailsErrors((prev) =>
        prev.map((category, i) =>
          i === categoryIndex
            ? {
                ...category,
                skills: category?.skills?.map((skill, j) =>
                  j === skillIndex
                    ? {
                        ...skill,
                        ...(field === "description" && { description: "" }),
                        ...(isNameAndEmpty && { name: "", description: "" }),
                      }
                    : skill
                ),
              }
            : category
        )
      );

      setProfessionalDetailsTouched((prev) =>
        prev.map((category, i) =>
          i === categoryIndex
            ? {
                ...category,
                skills: category?.skills?.map((skill, j) =>
                  j === skillIndex
                    ? {
                        ...skill,
                        ...(field === "description" && { description: false }),
                        ...(isNameAndEmpty && {
                          name: false,
                          description: false,
                        }),
                      }
                    : skill
                ),
              }
            : category
        )
      );
    }

    // Update actual professional details
    setProfessionalDetails((prev) =>
      prev.map((category, i) =>
        i === categoryIndex
          ? {
              ...category,
              skills: category?.skills?.map((skill, j) =>
                j === skillIndex
                  ? {
                      ...skill,
                      [field]: value,
                      ...(isNameAndEmpty && { description: "" }),
                    }
                  : skill
              ),
            }
          : category
      )
    );
  };

  // FUNCTION: Remove category
  const handleRemoveCategory = (index) => {
    if (professionalDetails.length === 1) return;
    setProfessionalDetails((prev) => prev?.filter((_, i) => i !== index));
  };

  // FUNCTION: Remove skill
  const handleRemoveSkill = (categoryIndex, skillIndex) => {
    setProfessionalDetails((prev) =>
      prev.map((category, i) =>
        i === categoryIndex
          ? {
              ...category,
              skills: category?.skills?.filter((_, j) => j !== skillIndex),
            }
          : category
      )
    );
  };

  // FUNCTION: Clear skill error
  const handleClearSkillError = (categoryIndex, skillIndex, field) => {
    setProfessionalDetailsErrors((prev) =>
      prev.map((category, i) =>
        i === categoryIndex
          ? {
              ...category,
              skills: category?.skills?.map((skill, j) =>
                j === skillIndex ? { ...skill, [field]: "" } : skill
              ),
            }
          : category
      )
    );

    setProfessionalDetailsTouched((prev) =>
      prev.map((category, i) =>
        i === categoryIndex
          ? {
              ...category,
              skills: category?.skills?.map((skill, j) =>
                j === skillIndex ? { ...skill, [field]: false } : skill
              ),
            }
          : category
      )
    );
  };

  // FUNCTION: Clear category error
  const handleClearCategoryError = (categoryIndex) => {
    setProfessionalDetailsErrors((prev) =>
      prev.map((category, i) =>
        i === categoryIndex
          ? {
              ...category,
              category: "",
              categoryName: "",
              skills: category?.skills?.map((skill) => ({
                name: "",
                description: "",
              })),
            }
          : category
      )
    );

    setProfessionalDetailsTouched((prev) =>
      prev.map((category, i) =>
        i === categoryIndex
          ? {
              ...category,
              category: false,
              categoryName: false,
              skills: category?.skills?.map((skill) => ({
                name: false,
                description: false,
              })),
            }
          : category
      )
    );
  };

  const filterCategories = () => {
    return categories?.filter(
      (category) =>
        !professionalDetails.find(
          (categoryDetail) => categoryDetail?.category?._id === category._id
        )
    );
  };

  const filterSkills = (categoryIndex) => {
    return professionalDetails[categoryIndex].category?.skills?.filter(
      (skill) =>
        !professionalDetails[categoryIndex].skills.find(
          (skillDetail) => skillDetail?.name === skill?.name
        )
    );
  };

  console.log('professio errors', professionalDetailsErrors);
  console.log('professio touched', professionalDetailsTouched);

  return (
    <div className="my-10">
      <FormSectionHeading title="Professional Details" index={3} />

      <div className="mt-6 relative h-fit">
        <div className=" ml-7 lg:ml-12">
          {professionalDetails?.map((categoryDetail, categoryIndex) => (
            <div key={categoryIndex} className=" mb-4">
              <div className="absolute left-1 w-[0.9375rem] h-[0.9375rem] bg-[#FBC948] rounded-full" />
              <div className=" relative w-full md:w-[29.6875rem]">
                {professionalDetails.length > 1 && (
                  <button
                    className=" absolute -top-1 -right-0 lg:-right-9 lg:top-9 text-[#C8CAD8] hover:text-red-500 transition duration-150 ease-in-out"
                    onClick={() => handleRemoveCategory(categoryIndex)}
                  >
                    <CiCircleRemove size={28} />
                  </button>
                )}
                <Dropdown
                  placeholder="Select a Category"
                  defaultOption={categoryDetail.category}
                  options={filterCategories()}
                  onSelect={(selectedCategory) =>
                    handleCategoryChange(categoryIndex, selectedCategory)
                  }
                  error={
                    professionalDetailsTouched[categoryIndex]?.category &&
                    professionalDetailsErrors[categoryIndex]?.category
                  }
                  onFocus={() => handleClearCategoryError(categoryIndex)}
                  outerContainerStyle="w-full md:w-[29.6875rem]"
                  isSearchable={true}
                  label="Category"
                  displayKey="name"
                  idKey="_id"
                  labelStyle="text-content"
                  buttonClassName="rounded-lg"
                  allowFillColor={false}
                  isRequired={true}
                />
              </div>

              {categoryDetail && (
                <div className="my-4 relative h-fit pb-6">
                  <label className="text-content font-inter font-medium text-sm">
                    Skills
                    <span className="text-light-red">*</span>
                  </label>
                  <div className="relative h-fit mt-2">
                    <div className="ml-6">
                      {categoryDetail.skills.map((skill, skillIndex) => (
                        <div
                          key={skillIndex}
                          className="flex flex-col gap-2 mb-4 "
                        >
                          <div className="absolute left-1 w-[0.9375rem] h-[0.9375rem] bg-[#FBC948] rounded-full" />
                          <div className=" relative w-full md:w-[29.6875rem]">
                            <button
                              className=" absolute -top-1 -right-0 lg:-right-9 font-bold lg:top-9 text-[#C8CAD8] hover:text-red-500 transition duration-150 ease-in-out"
                              onClick={() =>
                                handleRemoveSkill(categoryIndex, skillIndex)
                              }
                            >
                              <CiCircleRemove size={28} />
                            </button>
                            <Dropdown
                              placeholder="Select Skill"
                              options={filterSkills(categoryIndex)}
                              defaultOption={skill.name}
                              onSelect={(selectedSkill) =>
                                handleSkillChange(
                                  categoryIndex,
                                  skillIndex,
                                  "name",
                                  selectedSkill?.name
                                )
                              }
                              onFocus={() =>
                                handleClearSkillError(
                                  categoryIndex,
                                  skillIndex,
                                  "name"
                                )
                              }
                              error={
                                professionalDetailsTouched[categoryIndex]
                                  ?.skills[skillIndex]?.name &&
                                professionalDetailsErrors[categoryIndex]
                                  ?.skills[skillIndex]?.name
                              }
                              outerContainerStyle="w-full md:w-[29.6875rem]"
                              isSearchable={true}
                              label="Skill Name"
                              displayKey="name"
                              idKey="_id"
                              labelStyle="text-content"
                              buttonClassName="rounded-lg"
                              allowFillColor={false}
                              isRequired={true}
                            />
                          </div>

                          <CustomInput
                            label="Description"
                            isRequired={true}
                            placeholder="Enter Small Description"
                            labelStyle="text-content"
                            type="text"
                            value={skill.description}
                            onChange={(e) =>
                              handleSkillChange(
                                categoryIndex,
                                skillIndex,
                                "description",
                                e.target.value
                              )
                            }
                            error={
                              professionalDetailsTouched[categoryIndex]?.skills[
                                skillIndex
                              ]?.description &&
                              professionalDetailsErrors[categoryIndex]?.skills[
                                skillIndex
                              ]?.description
                            }
                            className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleAddSkill(categoryIndex)}
                        className="text-primary flex items-center gap-1 font-inter text-dark text-[0.875rem] font-semibold"
                      >
                        <FaPlus />
                        Add Skill
                      </button>
                    </div>

                    <div className="absolute left-[0.625rem] top-0 w-[0.125rem] h-full bg-[#FBC948] bg-opacity-[24%]" />
                    <div className="absolute left-1 bottom-[0.125rem] w-[0.9375rem] h-[0.9375rem] bg-[#FBC948] rounded-full" />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* // INFO: If first category is empty then do not show add new category button */}
          {professionalDetails[0]?.category && (
            <button
              type="button"
              onClick={handleAddCategory}
              className="flex items-center gap-1 font-inter text-dark text-sm font-semibold"
            >
              <FaPlus />
              Add Category
            </button>
          )}
        </div>
        <div className="absolute left-1 bottom-0 w-[0.9375rem] h-[0.9375rem] bg-[#FBC948] rounded-full" />
        <div className="absolute left-[0.625rem] top-0 w-[0.125rem] h-full bg-[#FBC948] bg-opacity-[24%]" />
      </div>
    </div>
  );
};

export default ProfessionalDetailsSection;
